/**
 * IDs de perguntas e Campos Adicionais usados pelo PGR customizado do Bradesco.
 *
 * Fonte: especificação funcional validada por Julia Buzzo (PM) em 2026-08-24
 * (canal #desenvolvimento-saas, thread da demanda "PDF Customizado PGR Bradesco").
 *
 * Todos os vetores abaixo cujo comentário diz "pareado por posição" DEVEM manter a
 * mesma ordem entre si: a posição `i` de `CARGO_QUESTION_IDS` é o mesmo cargo/registro
 * da posição `i` de `NUM_FUNCIONARIOS_QUESTION_IDS` e de `RISCO_DO_CARGO_QUESTION_IDS`.
 */

// Campos Adicionais do Local (Place)
export const LOCAL_FIELD_ID_ENDERECO = 1
export const LOCAL_FIELD_ID_CNPJ = 2
export const LOCAL_FIELD_ID_QTD_FUNCIONARIOS = 5

// Dependência da tabela 1.2 NÃO é Campo Adicional — é NÍVEL do Local da atividade na
// hierarquia de "nível de local" (`place.placeLevelsResume`, ver `ServiceOrderLoader.js`).
// Confirmado por Julia Buzzo (PM, 2026-08-28), corrigindo o entendimento anterior desta
// mesma revisão (Campo Adicional IDs 3/4), que a auditoria campo-a-campo já havia flagado
// como divergente do documento de mapeamento do cliente.
//
// Convenção de indexação (NÍVEL N, 1-indexado no documento do cliente -> posição N-1 no
// array `placeLevelsResume`, 0-indexado): mesma convenção já usada em todo o app para
// "nível de local" — ver `DEFAULT_LEVELS_NAMES = ['level 01', 'level 02', 'level 03',
// 'level 04']` em `containers/places/filters/PlacesFilterGroupItems.js` (nomes-padrão de
// nível quando a conta não os renomeia — 1-indexados na label, 0-indexados na posição do
// array) e o mesmo padrão `place.placeLevelsResume[N].name` já em produção nos PDFs
// white-label segurpro/comercial/allarmi (ver `_shared/sections/ClientData.js`).
export const LOCAL_PLACE_LEVEL_INDEX_DEPENDENCIA = 0 // NÍVEL 1

// JUNÇÃO — causa raiz definitiva encontrada em 2026-09-02, ver
// `01.InformacoesIniciaisSection.js` para a explicação completa. RESUMO: Junção NÃO É um
// nível de hierarquia (`placeLevelsResume`) — é `serviceOrder.place.name` diretamente, o
// mesmo campo já usado (e já corrigido) na capa (`getDestinoDaAtividade`,
// `pdfHelpers.js`) e que a coluna EMPRESA usava por engano antes de ser trocada por
// `EMPRESA_FIXO` (ver comentário de `EMPRESA_FIXO` abaixo — o valor deslocado daquele bug,
// ex. "2160", é exatamente o valor que deveria ir para Junção). Não existe mais nenhuma
// constante de índice para Junção — histórico completo das 3 tentativas anteriores
// (índice fixo "Nível 4" -> índice dinâmico "último nível" -> de volta ao índice fixo ->
// abandono total do modelo de nível de hierarquia) preservado no histórico do Git deste
// arquivo para quem precisar entender o percurso.

// Campo Adicional do Ativo (Cargo)
export const ATIVO_FIELD_ID_DESCRICAO_CARGO = 6

// Campo Adicional da CONTA/USUÁRIO que executou a atividade (Seção 21 - Certificado de
// Calibração). Diferente dos campos de Local/Ativo acima (IDs 1-6, todos com ID numérico
// confirmado na spec de Julia Buzzo de 2026-08-24), este campo foi pedido depois — Julia
// Buzzo (PM, 2026-08-27): "o certificado de calibração tem que vir do usuário que executou
// [a atividade], não da atividade". ID do campo confirmado por Julia Buzzo (PM,
// 2026-08-28): 7. Casado por ID (ver `getExecutorCertificadoCalibracao` em
// `certificadoEngine.js`), mesmo padrão dos demais campos numéricos acima.
export const USER_FIELD_ID_CERTIFICADO_CALIBRACAO = 7

// Setor — DUAS perguntas cujas respostas juntas representam a lista de Setores da
// atividade, confirmado por Julia Buzzo (PM, 2026-09-03), substituindo qualquer suposição
// anterior: 256 é a pergunta "Quais os setores existem nesta dependência?" (checkbox,
// multi-select, lista fixa de opções pré-definidas) e 354 é "Nome do setor" (texto livre,
// dentro do fluxo alternativo "Deseja inserir avaliação de mais algum setor?" -> "Sim" ->
// "Adicione os setores" -> "Adicionar novo item", que permite cadastrar um Setor
// adicional por nome digitado em vez de escolher da lista fixa de 256). TODAS as
// respostas de AMBAS as perguntas, juntas, formam a lista de Setores da atividade — em
// qualquer parte do documento que use/referencie Setor (Seção 5.3, Seção 18/Inventário
// de Riscos), nunca só 256.
//
// Causa raiz do entendimento anterior (só 256): o comentário original desta constante
// dizia "Setor (pergunta única da atividade, bloco 354)" — o "354" já estava anotado
// aqui, mas foi lido como uma anotação de BLOCO da pergunta 256, não como uma SEGUNDA
// pergunta de Setor. Era isso mesmo desde o início.
export const SETOR_QUESTION_IDS = [256, 354]

// Cargo — 20 posições. Cada posição é um "cargo" tratado individualmente (GHE = Setor + Cargo).
export const CARGO_QUESTION_IDS = [
  662, 697, 716, 735, 754, 773, 792, 811, 830, 849, 868, 887, 906, 925, 944, 963, 982, 1001, 1020, 681
]

// Nº de Funcionários Expostos — pareado por posição com CARGO_QUESTION_IDS.
export const NUM_FUNCIONARIOS_QUESTION_IDS = [
  1038, 1039, 1040, 1041, 1042, 1043, 1044, 1045, 1046, 1047, 1048, 1049, 1050, 1051, 1052, 1053, 1054, 1055, 1056, 1057
]

// Risco do Cargo — pareado por posição com CARGO_QUESTION_IDS.
// Observação: a lista fornecida traz 19 IDs para 20 cargos (falta o par da última posição,
// cargo de ID 681). Mantido exatamente como recebido.
//
// NÃO É a coluna "Tipo de exposição" da Seção 18 (Inventário de Riscos): confirmado por
// inspeção pixel-a-pixel do modelo de referência (páginas 42-51) que "Tipo de exposição" é
// texto FIXO por linha de risco (ex.: "Ergonômico (conforto)" para Ruído/Temperatura/
// Iluminância, "Acidente" para Batidas contra/Quedas — ver `_helpers/inventarioRiscosFixo.js`),
// nunca a resposta desta pergunta. Um mapeamento anterior usava esta lista para essa coluna;
// corrigido nesta revisão. `RISCO_DO_CARGO_QUESTION_IDS` fica mantido aqui (mapeamento da
// spec original, seção 8) para o caso de ainda ser necessário em outro ponto do documento,
// mas não é mais lido por `cargoEngine.js`/Seção 18.
export const RISCO_DO_CARGO_QUESTION_IDS = [
  683, 698, 717, 736, 755, 774, 793, 812, 831, 850, 869, 888, 907, 926, 945, 964, 983, 1002, 1021
]

// DEPRECIADO (2026-09-09) — NÃO USAR. Confirmado por consulta direta ao banco de dados
// do workspace "bradesco" (tabela `checkList`, serviceId=2) que estes 3 arrays apontam
// para uma árvore de perguntas "Possível Risco" (tipo `asset`) completamente diferente,
// sem NENHUMA relação com `CARGO_QUESTION_IDS`/"Riscos do cargo" — nem um único ID
// destas 3 listas aparece como descendente real de nenhum dos 20 Cargos verificados.
// `cargoEngine.js#buildLinhaConforto` não usa mais estes arrays: Ruído/Temperatura/
// Iluminância agora são resolvidos por casamento estrutural (nome + árvore de
// descendentes do Cargo), o mesmo padrão já usado para Biomecânico/Acidente/
// Psicossocial — ver o comentário ATUALIZAÇÃO em `buildLinhaConforto` para os detalhes
// completos. Mantidos aqui (não apagados) só para preservar o histórico de investigação;
// não reintroduzir seu uso.

// Conforto — Ruído (dB). Regra: valor <= 65 => Probabilidade "Muito Baixo" / Severidade "Irrelevante".
export const RUIDO_QUESTION_IDS = [
  276, 684, 688, 692, 699, 703, 707, 711, 718, 722, 726, 730, 737, 741, 745, 749, 756, 760, 764, 768, 775, 779, 783,
  787, 794, 798, 802, 806, 813, 817, 821, 825, 832, 836, 840, 844, 851, 855, 859, 863, 870, 874, 878, 882, 889, 893,
  897, 901, 908, 912, 916, 920, 927, 931, 935, 939, 946, 950, 954, 958, 965, 969, 973, 977, 984, 988, 992, 996, 1003,
  1007, 1011, 1015, 1022, 1026, 1030, 1034, 356
]

// Conforto — Temperatura (°C). Regra: 18 <= valor <= 25 => "Muito Baixo" / "Irrelevante".
export const TEMPERATURA_QUESTION_IDS = [
  295, 685, 689, 693, 700, 704, 708, 712, 719, 723, 727, 731, 738, 742, 746, 750, 757, 761, 765, 769, 776, 780, 784,
  788, 795, 799, 803, 807, 814, 818, 822, 826, 833, 837, 841, 845, 852, 856, 860, 864, 871, 875, 879, 883, 890, 894,
  898, 902, 909, 913, 917, 921, 928, 932, 936, 940, 947, 951, 955, 959, 966, 970, 974, 978, 985, 989, 993, 997, 1004,
  1008, 1012, 1016, 1023, 1027, 1031, 1035, 357
]

// Conforto — Iluminância. Regra: valor >= 500 => "Muito Baixo" / "Irrelevante".
export const ILUMINANCIA_QUESTION_IDS = [
  297, 687, 691, 695, 702, 706, 710, 714, 721, 725, 729, 733, 740, 744, 748, 752, 759, 763, 767, 771, 778, 782, 786,
  790, 797, 801, 805, 809, 816, 820, 824, 828, 835, 839, 843, 847, 854, 858, 862, 866, 873, 877, 881, 885, 892, 896,
  900, 904, 911, 915, 919, 923, 930, 934, 938, 942, 949, 953, 957, 961, 968, 972, 976, 980, 987, 991, 995, 999, 1006,
  1010, 1014, 1018, 1025, 1029, 1033, 1037, 359
]

// "Possível Risco" — pergunta multi-select (Biomecânico, Acidente, Psicossocial = "Tipo 2").
export const POSSIVEL_RISCO_QUESTION_IDS = [
  1131, 1152, 1163, 1176, 1183, 1196, 1203, 1216, 1223, 1236, 1243, 1256, 1263, 1276, 1283, 1296, 1303, 1316, 1323,
  1334, 1341, 1354, 1361, 1374, 1381, 1394, 1401, 1414, 1421, 1434, 1441, 1454, 1461, 1474, 1481, 1494, 1501, 1514
]

// Inventário de Riscos — coluna Probabilidade (pareada por posição com a coluna Severidade
// abaixo). ATUALIZADO (2026-09-04): Julia Buzzo (PM) enviou um documento formal ("18.
// Inventário de Riscos") especificando 240 IDs (antes 170) para esta coluna — os 170
// IDs anteriores são um PREFIXO EXATO desta lista nova (confirmado programaticamente,
// zero divergência na faixa em comum); os 70 IDs adicionais no final cobrem mais
// instâncias de Cargo do que a spec original previa. Vetores de referência e a matriz de
// risco (`riskEngine.js`) NÃO mudaram — só a lista de IDs de origem. Fonte de verdade:
// especificação formal da Julia, não suposição/dedução do código.
export const INVENTARIO_PROBABILIDADE_QUESTION_IDS = [
  1132, 1134, 1136, 1138, 1140, 1146, 1153, 1155, 1157, 1164, 1166, 1168, 1170, 1172, 1174, 1177, 1179, 1181, 1184,
  1186, 1188, 1190, 1192, 1194, 1197, 1199, 1201, 1204, 1206, 1208, 1210, 1212, 1214, 1217, 1219, 1221, 1224, 1226,
  1228, 1230, 1232, 1234, 1237, 1239, 1241, 1244, 1246, 1248, 1250, 1252, 1254, 1257, 1259, 1261, 1264, 1266, 1268,
  1270, 1272, 1274, 1277, 1279, 1281, 1284, 1286, 1288, 1290, 1292, 1294, 1297, 1299, 1301, 1304, 1306, 1308, 1310,
  1312, 1314, 1317, 1319, 1321, 1324, 1326, 1328, 1330, 1332, 1335, 1337, 1339, 1342, 1344, 1346, 1348, 1350, 1352,
  1355, 1357, 1359, 1362, 1364, 1366, 1368, 1370, 1372, 1375, 1377, 1379, 1382, 1384, 1386, 1388, 1390, 1392, 1395,
  1397, 1399, 1402, 1404, 1406, 1408, 1410, 1412, 1415, 1417, 1419, 1422, 1424, 1426, 1428, 1430, 1432, 1435, 1437,
  1439, 1442, 1444, 1446, 1448, 1450, 1452, 1455, 1457, 1459, 1462, 1464, 1466, 1468, 1470, 1472, 1475, 1477, 1479,
  1482, 1484, 1486, 1488, 1490, 1492, 1495, 1497, 1499, 1502, 1504, 1506, 1508, 1510, 1512, 1515, 1517, 1519, 1521,
  1524, 1526, 1528, 1530, 1532, 1534, 1537, 1539, 1541, 1543, 1544, 1547, 1551, 1554, 1557, 1560, 1563, 1566, 1569,
  1572, 1575, 1578, 1581, 1584, 1587, 1590, 1593, 1596, 1599, 1602, 1605, 1608, 1611, 1614, 1617, 1620, 1623, 1626,
  1629, 1632, 1635, 1638, 1641, 1644, 1647, 1650, 1653, 1656, 1659, 1662, 1665, 1668, 1671, 1674, 1677, 1680, 1683,
  1686, 1689, 1692, 1695, 1698, 1701, 1704, 1707, 1710, 1713, 1716, 1719
]

// Inventário de Riscos — coluna Severidade (pareada por posição com a coluna Probabilidade
// acima). Mesma atualização de 240 IDs (antes 170) descrita no comentário de
// `INVENTARIO_PROBABILIDADE_QUESTION_IDS` acima — mesma fonte, mesma verificação de
// prefixo exato.
export const INVENTARIO_SEVERIDADE_QUESTION_IDS = [
  1133, 1135, 1137, 1139, 1141, 1147, 1154, 1156, 1158, 1165, 1167, 1169, 1171, 1173, 1175, 1178, 1180, 1182, 1185,
  1187, 1189, 1191, 1193, 1195, 1198, 1200, 1202, 1205, 1207, 1209, 1211, 1213, 1215, 1218, 1220, 1222, 1225, 1227,
  1229, 1231, 1233, 1235, 1238, 1240, 1242, 1245, 1247, 1249, 1251, 1253, 1255, 1258, 1260, 1262, 1265, 1267, 1269,
  1271, 1273, 1275, 1278, 1280, 1282, 1285, 1287, 1289, 1291, 1293, 1295, 1298, 1300, 1302, 1305, 1307, 1309, 1311,
  1313, 1315, 1318, 1320, 1322, 1325, 1327, 1329, 1331, 1333, 1336, 1338, 1340, 1343, 1345, 1347, 1349, 1351, 1353,
  1356, 1358, 1360, 1363, 1365, 1367, 1369, 1371, 1373, 1376, 1378, 1380, 1383, 1385, 1387, 1389, 1391, 1393, 1396,
  1398, 1400, 1403, 1405, 1407, 1409, 1411, 1413, 1416, 1418, 1420, 1423, 1425, 1427, 1429, 1431, 1433, 1436, 1438,
  1440, 1443, 1445, 1447, 1449, 1451, 1453, 1456, 1458, 1460, 1463, 1465, 1467, 1469, 1471, 1473, 1476, 1478, 1480,
  1483, 1485, 1487, 1489, 1491, 1493, 1496, 1498, 1500, 1503, 1505, 1507, 1509, 1511, 1513, 1516, 1518, 1520, 1522,
  1525, 1527, 1529, 1531, 1533, 1535, 1538, 1540, 1542, 1545, 1548, 1549, 1552, 1555, 1558, 1561, 1564, 1567, 1570,
  1573, 1576, 1579, 1582, 1585, 1588, 1591, 1594, 1597, 1600, 1603, 1606, 1609, 1612, 1615, 1618, 1621, 1624, 1627,
  1630, 1633, 1636, 1639, 1642, 1645, 1648, 1651, 1654, 1657, 1660, 1663, 1666, 1669, 1672, 1675, 1678, 1681, 1684,
  1687, 1690, 1693, 1696, 1699, 1702, 1705, 1708, 1711, 1714, 1717, 1720
]

// CNAE / ramo de atividade / grau de risco — texto fixo do documento (não são campos dinâmicos;
// confirmado pelo template de referência bioqualynet/pgr, que traz os mesmos valores hardcoded
// para o mesmo ramo "Bancos Múltiplos com Carteira Comercial").
export const CNAE_FIXO = '64.22-1-00'
export const RAMO_ATIVIDADE_FIXO = 'Bancos Múltiplos com Carteira Comercial'
export const GRAU_DE_RISCO_FIXO = '01'

// Coluna "EMPRESA" da tabela 1.2 (Identificação das Dependências) — SEMPRE "Bradesco" neste
// documento (nome do cliente/contrato, não dado dinâmico da atividade). Bug reportado por
// Julia Buzzo (PM, 2026-09-01): a implementação anterior usava `serviceOrder.place?.name`
// (o código numérico da agência, ex. "2160") nessa coluna — um Local não é uma Empresa;
// não há (e não deveria haver) nenhum campo dinâmico de "Empresa" na atividade para este
// PDF, já que o próprio cliente É o Bradesco em todos os casos deste documento (mesmo
// padrão de CNAE_FIXO/RAMO_ATIVIDADE_FIXO/GRAU_DE_RISCO_FIXO acima: texto fixo, não Campo
// Adicional nem nível de Local). NOTA (2026-09-02): o valor deslocado por este bug
// (`place.name`, ex. "2160") não era lixo — era o valor de JUNÇÃO, ver comentário "JUNÇÃO"
// mais acima neste arquivo para a causa raiz completa dessa coluna.
//
// Checado contra o PDF-modelo (2026-09-01, página 6): a anotação da própria página, sob a
// coluna EMPRESA, diz literalmente "LOCAL DA ATIVIDADE" (ao contrário das outras 3 colunas
// da mesma tabela, que citam um ID de Campo Adicional específico) — o que é provavelmente
// a origem histórica deste bug (implementação anterior seguiu essa nota ao pé da letra).
// Julia corrigiu essa mesma nota diretamente (2026-09-01, com exemplo concreto "Empresa =
// Bradesco, sempre"), no mesmo padrão já visto com JUNÇÃO/DEPENDÊNCIAS: a nota do
// documento-modelo dizia "CAMPO ADICIONAL DO LOCAL - ID: 3/4" para essas colunas, e a
// Julia já tinha corrigido isso em 2026-08-28 para NÍVEL de Local em vez de Campo
// Adicional (ver comentário acima). A instrução direta e datada da Julia prevalece sobre
// a nota genérica do template nos dois casos.
export const EMPRESA_FIXO = 'Bradesco'
